import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { HashLink } from 'react-router-hash-link';

import "./Navbar.css";
import axios from "axios";


const Navbar = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [userName, setUserName] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userPayments, setUserPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [userInvLink, setUserInvLink] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  useEffect(() => {
    // Global axios 401 handler (register once)
    if (!window.__axios401HandlerRegistered) {
      window.__axios401HandlerRegistered = true;
      axios.interceptors.response.use(
        (response) => response,
        (error) => {
          const status = error?.response?.status;
          if (status === 401) {
            try {
              sessionStorage.removeItem('token');
              sessionStorage.removeItem('uid');
              sessionStorage.removeItem('fullName');
              sessionStorage.removeItem('email');
              sessionStorage.removeItem('country');
              sessionStorage.removeItem('phone');
              localStorage.removeItem('token');
              localStorage.removeItem('uid');
            } catch (e) { }
            const base = window.location.pathname.includes('/stis2025') ? '/stis2025' : '';
            window.location.href = `${base}/login-signup`;
          }
          return Promise.reject(error);
        }
      );
    }

    const fullName = sessionStorage.getItem("fullName");
    setUserName(fullName || null);

    // Fetch user payment data if user is logged in
    if (fullName) {
      const token = sessionStorage.getItem('token');
      const uid = sessionStorage.getItem('uid');

      if (token && uid) {
        axios
          .get(`https://api.rescons.in/user-info/${uid}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => {
            const user = res.data;
            setUserPayments(user?.payments || []);
            // Check if user has inv field (invitation letter link)
            if (user?.inv) {
              setUserInvLink(user.inv);
            }
            // Also store in sessionStorage for other components
            sessionStorage.setItem('userPayments', JSON.stringify(user?.payments || []));
          })
          .catch((err) => {
            console.error("Error fetching user info in Navbar:", err);
          });
      }
    }
  }, [location]);

  useEffect(() => {
    const fetchUserPayments = async () => {
      const uid = sessionStorage.getItem('uid');
      if (uid) {
        try {
          const response = await axios.get(`https://api.rescons.in/user-info/${uid}`);
          if (response.data) {
            if (response.data.payments) {
              setUserPayments(response.data.payments);
            }
            // Check if user has inv field (invitation letter link)
            if (response.data.inv) {
              setUserInvLink(response.data.inv);
            }
          }
        } catch (error) {
          console.error("Error fetching user payments:", error);
        }
      }
      setLoadingPayments(false);
    };

    fetchUserPayments();

    // Listen for payment updates
    const handlePaymentUpdate = (event) => {
      const uid = sessionStorage.getItem('uid');
      if (uid && event.detail.uid === uid) {
        fetchUserPayments();
      }
    };

    window.addEventListener('paymentUpdated', handlePaymentUpdate);

    return () => {
      window.removeEventListener('paymentUpdated', handlePaymentUpdate);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    setUserName(null);
    setUserInvLink(null);
    navigate("/");
  };

  const handleDownloadInvitation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (userInvLink) {
      // Open the PDF in a new browser window/tab
      window.open(userInvLink, '_blank', 'noopener,noreferrer');
      setIsUserMenuOpen(false);
    }
  };

  const handleNavClick = (path) => {
    setIsMobileOpen(false);
    navigate(path);
    window.scrollTo(0, 0);
  };



  const renderDropdown = (label, items) => (
    <div className="navbar-dropdown">
      <span className="dropdown-toggle">{label}</span>
      <div className="dropdown-menu">
        {items.map((item, idx) => {
          if (item.download) {
            // If it's a downloadable file
            return (
              <a key={idx} href={item.to} download>
                {item.label}
              </a>
            );
          } else if (item.to.startsWith("/#")) {
            // If it's a hash link (scroll to section)
            return (
              <HashLink
                key={idx}
                smooth
                to={item.to}
                onClick={() => handleNavClick(item.to)}
              >
                {item.label}
              </HashLink>
            );
          } else {
            // Otherwise, use normal NavLink for routing
            return (
              <NavLink
                key={idx}
                to={item.to}
                onClick={() => handleNavClick(item.to)}
              >
                {item.label}
              </NavLink>
            );
          }
        })}
      </div>
    </div>
  );


  return (
    <header className="navbar">
      {/* Registration Pending Banner */}
      {userName && !loadingPayments && userPayments && !userPayments.some(payment => payment.status === 'captured' || payment.status === 'paid') && (
        <div style={{
          backgroundColor: '#dc2626',
          color: 'white',
          textAlign: 'center',
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: 'bold',
          borderBottom: '2px solid #b91c1c',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          ⚠️ Registration Pending - Please Complete the Payment Process
        </div>
      )}

      <div className="navbar-top">
        <div className="navbar-brand" onClick={() => handleNavClick("/")}>
          <img
            src="https://iisc.ac.in/wp-content/themes/iisc/images/favicon/apple-icon-57x57.png"
            alt="STIS-V"
            className="brand-icon"
          />
          <span>STIS-V 2025</span>
        </div>
        <div className="organizer-block">
          <div className="organizer-label">Organized by</div>
          <div className="organizer-content">
            <img
              src="https://iisc.ac.in/wp-content/uploads/2020/08/IISc_Master_Seal.jpg"
              alt="IISc"
              className="org-logo"
            />
            <span>Indian Institute of Science</span>
          </div>
        </div>

        <div className="coorganizer-block">
          <div className="organizer-label">Co-organizers</div>
          <div className="organizer-content">
            <img
              src="/stis2025/assets/JSw.png"
              alt="JSW Steel"
              className="org-logo"

            />
            <span>JSW Steel</span>
          </div>
        </div>

        {/* Desktop Auth Buttons */}
        <div className="navbar-auth">
          {userName ? (
            <div
              className={`navbar-user-dropdown${isUserMenuOpen ? " open" : ""}`}
              ref={userMenuRef}
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setIsUserMenuOpen((open) => !open);
              }}
            >
              <div className="user-badge">
                Welcome, {userName.toUpperCase()}
              </div>
              <div className="user-dropdown-menu">
                <NavLink
                  to="/abstract-submission"
                  onClick={() => handleNavClick("/abstract-submission")}
                >
                  My Submissions
                </NavLink>
                <NavLink
                  to="/registration-form"
                  onClick={() => handleNavClick("/registration-form")}
                >
                  Registration Payment
                </NavLink>
                <NavLink
                  to="/my-payments"
                  onClick={() => handleNavClick("/my-payments")}
                >
                  My Payments
                </NavLink>

                {/* Download Invitation Letter - Show if user has inv field */}
                {userInvLink && (
                  <a
                    href="#"
                    onClick={handleDownloadInvitation}
                    className="download-invitation-link"
                    style={{
                      borderTop: '1px solid #e0e0e0',
                      marginTop: '8px',
                      paddingTop: '12px'
                    }}
                  >
                    📄 View Invitation Letter
                  </a>
                )}

                {(() => {
                  // Check if user's country is NOT India (show invitation letter for international users)
                  const userCountry = sessionStorage.getItem('country');
                  const hasCapturedPayment = userPayments && userPayments.some(payment => payment.status === 'captured' || payment.status === 'paid');
                  const shouldShow = userCountry && userCountry !== 'India';
                  const isDisabled = shouldShow && !hasCapturedPayment && !loadingPayments;

                  console.log("🔍 Navbar - User country:", userCountry, "Has captured payment:", hasCapturedPayment, "Loading payments:", loadingPayments, "Show invitation letter:", shouldShow, "Disabled:", isDisabled);

                  if (!shouldShow) return false;

                  return (
                    <div style={{ position: 'relative' }}>

                      {isDisabled && (
                        <div
                          className="tooltip-message"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '0',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                            border: '1px solid #b91c1c',
                            display: 'none'
                          }}>
                          ⚠️ Please Complete registration process
                        </div>


                      )}
                    </div>
                  );
                })()}
                <div className="logout-btn" onClick={handleLogout}>
                  Logout
                </div>
                {userPayments && userPayments.some(payment => payment.status === "captured" || payment.status === 'paid') && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      color: "green",
                      fontWeight: "bold",
                      marginTop: "8px",
                      padding: "6px 0",
                    }}
                  >
                    <span style={{ fontSize: "16px" }}>✔️</span>
                    <span>Registration Confirmed</span>
                  </div>
                )}

              </div>
            </div>
          ) : (
            <NavLink
              to="/login-signup"
              onClick={() => handleNavClick("/login-signup")}
            >
              Login / Signup
            </NavLink>
          )}


        </div>

        {/* Mobile Toggle Button */}
        <div className="navbar-toggle" onClick={toggleMobileMenu}>
          ☰
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className={`navbar-menu ${isMobileOpen ? "active" : ""}`}>
        <NavLink to="/" end onClick={() => handleNavClick("/")}>Home</NavLink>

        {renderDropdown("About", [
          { to: "/about", label: "About the Conference" },
          { to: "/message-to-chairman", label: "Chairman's Message" },
          { to: "/official-language", label: "Official Language" },

          { to: "/#sponsors", label: "Sponsors", isHash: true },
          { to: "/#mediapartners", label: "Media Partner", isHash: true }
        ])}

        <NavLink to="/distinguished-speaker" end onClick={() => handleNavClick("/distinguished-speaker")}>Speakers</NavLink>

        {renderDropdown("Programme", [
          { to: "/announcements", label: "Latest Updates" },
          { to: "/conference-themes", label: "Themes & Topics" },
          { to: "/conference-schedule", label: "Schedule" },
          { to: "/conference-proceedings", label: "Conference Proceedings" },
          { to: "/programme", label: "Important Dates" },

        ])}



        <NavLink to="/abstract-submission" onClick={() => handleNavClick("/abstract-submission")}>
          Paper Submission
        </NavLink>

        {userName ? (
          renderDropdown("Registration", [
            { to: "/conference-registration", label: "Registration Information" },
            { to: "/registration-form", label: "Registration Payment" },
            { to: "/invitation-letter-info", label: "Invitation Letter Information" },
            { to: "/my-payments", label: "My Payments" }
            // { to: "/payment-receipts", label: "Payment Receipts" }
          ])
        ) : (
          renderDropdown("Registration", [
            { to: "/conference-registration", label: "Registration Information" },
            { to: "/registration-form", label: "Registration Payment" },
            { to: "/invitation-letter-info", label: "Invitation Letter Information" }
          ])
        )}


        <NavLink to="/sponsorship-opportunities" onClick={() => handleNavClick("/sponsorship-opportunities")}>
          Sponsorship Opportunities
        </NavLink>

        <NavLink to="/committee" onClick={() => handleNavClick("/committee")}>
          Committee
        </NavLink>

        <NavLink to="/contact" onClick={() => handleNavClick("/contact")}>
          Contact Us
        </NavLink>

        {renderDropdown("Venue", [
          { to: "/venue", label: "Conference Venue" },
          { to: "/reach-iisc", label: "Reach IISc" },
          { to: "/accomodation", label: "Accommodation" }
        ])}

        {renderDropdown("Information", [
          { to: "/announcements", label: "Latest Updates" },
          { to: "/programme", label: "Important Dates" },
          { to: "/about-bengaluru", label: "About Bengaluru" },
          { to: "/weather", label: "Weather" },
          { to: "/travel-information", label: "Travel Information" },
          { to: "/tours-and-social-events", label: "Tours & Social Events" }
        ])}

        {renderDropdown("Downloads", [
          { to: "assets/Paper-Template.docx", label: "Paper Template", download: true },
          { to: "/stis2025/assets/1stAnnouncement.pdf", label: "First Announcement", download: true },
          { to: "/stis2025/assets/2ndAnnouncement.pdf", label: "Final Announcement", download: true },
          { to: "/stis2025/assets/callfor.pdf", label: "Call For Sponsorship", download: true },
          { to: "assets/Letter-From-MEA.pdf", label: "Letter from MEA", download: true },
          { to: "assets/schd.pdf", label: "Tentative Schedule", download: true },

        ])}


        {/* Mobile Auth Buttons */}
        {isMobileOpen && (
          <div className="navbar-auth-mobile">
            {userName ? (
              <div className="navbar-user">
                <span className="user-badge">
                  {userName.split(" ").map(w => w[0]).join("").toUpperCase()}
                </span>
                <a className="logout-btn" onClick={handleLogout}>Logout</a>
              </div>
            ) : (
              <NavLink to="/login-signup" onClick={() => handleNavClick("/login-signup")}>
                Login / Signup
              </NavLink>
            )}
          </div>
        )}
      </nav>

      <div className="paper-deadline-banner zoom-animation">
        📢 Tentative Technical Program Schedule is Now Published
        {/* <span className="highlight"> Last Date Was: 2 October 2025 </span> */}
      </div>
    </header>
  );
};

export default Navbar;
